import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

/** generateUUID() fallback for insecure contexts (HTTP) */
function generateUUID(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (
      +c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
    ).toString(16),
  );
}
import type {
  ConnectionConfig,
  ConnectionInsert,
  ConnectionUpdate,
  ConnectionStatus,
  DeviceStateStore,
  Profile,
  ProfileInsert,
  PhysicalConnection,
  PhysicalConnectionInsert,
} from "@production-hub/shared";
import type {
  LayoutWithChildren,
  StudioConfigData,
} from "@production-hub/shared";
import {
  useBridgeConnections,
  useConnectDevice,
  useDisconnectDevice,
  type ConnectionWithStatus,
} from "./useBridgeConnections";
import {
  useSingleStudioConfig,
  useUpdateStudioConfig,
} from "./useStudioConfigs";
import {
  useBridgePhysicalConnections,
  useCreatePhysicalConnection,
  useDeletePhysicalConnection,
} from "./useBridgePhysicalConnections";
import {
  useCreateStudioConnection,
  useDeleteStudioConnection,
  useStudioConnections,
  useUpdateStudioConnection,
} from "./useStudioConnections";

export type ConfigDataMode = "online" | "offline";

export interface UseConfigDataReturn {
  mode: ConfigDataMode;
  isLoading: boolean;

  // Connections
  connections: (ConnectionConfig & { status?: ConnectionStatus })[];
  addConnection: (data: ConnectionInsert) => void;
  updateConnection: (id: string, data: ConnectionUpdate) => void;
  deleteConnection: (id: string) => void;
  isConnectionMutating: boolean;

  // Online-only connection features
  connectDevice?: (id: string) => void;
  disconnectDevice?: (id: string) => void;
  isConnecting?: boolean;
  isDisconnecting?: boolean;

  // Layouts
  layouts: LayoutWithChildren[];
  addLayout: (data: { name: string; description?: string }) => void;
  deleteLayout: (id: string) => void;

  // Profiles
  profiles: Profile[];
  addProfile: (data: ProfileInsert) => void;
  deleteProfile: (id: string) => void;

  // Physical connections (wiring)
  physicalConnections: PhysicalConnection[];
  addPhysicalConnection: (data: PhysicalConnectionInsert) => void;
  deletePhysicalConnection: (id: string) => void;

  // Device state (generic key-value store)
  getDeviceState: (connectionId: string) => DeviceStateStore | undefined;
  setDeviceState: (connectionId: string, store: DeviceStateStore) => void;
  updateDeviceStateValue: (
    connectionId: string,
    path: string,
    value: string | number | boolean,
  ) => void;

  isMutating: boolean;
}

interface UseConfigDataOptions {
  configId: string;
  studioId: string;
  bridgeUrl: string | null | undefined;
  bridgeOnline: boolean;
  isActiveConfig: boolean;
}

// ============ Bridge layout/profile queries ============

const useBridgeLayouts = (
  bridgeUrl: string | null | undefined,
  enabled: boolean,
) =>
  useQuery({
    queryKey: ["bridge-layouts", bridgeUrl],
    queryFn: async (): Promise<LayoutWithChildren[]> => {
      const res = await fetch(`${bridgeUrl}/api/layouts`);
      if (!res.ok) throw new Error(`Failed to fetch layouts: ${res.status}`);
      const layouts = await res.json();
      // Fetch boxes and presets for each layout
      const withChildren = await Promise.all(
        layouts.map(async (layout: LayoutWithChildren) => {
          const [boxesRes, presetsRes] = await Promise.all([
            fetch(`${bridgeUrl}/api/layouts/${layout.id}/boxes`),
            fetch(`${bridgeUrl}/api/layouts/${layout.id}/presets`),
          ]);
          const boxes = boxesRes.ok ? await boxesRes.json() : [];
          const presets = presetsRes.ok ? await presetsRes.json() : [];
          return { ...layout, boxes, presets };
        }),
      );
      return withChildren;
    },
    enabled: !!bridgeUrl && enabled,
    refetchInterval: 5000,
  });

const useBridgeProfiles = (
  bridgeUrl: string | null | undefined,
  enabled: boolean,
) =>
  useQuery({
    queryKey: ["bridge-profiles", bridgeUrl],
    queryFn: async (): Promise<Profile[]> => {
      const res = await fetch(`${bridgeUrl}/api/profiles`);
      if (!res.ok) throw new Error(`Failed to fetch profiles: ${res.status}`);
      return res.json();
    },
    enabled: !!bridgeUrl && enabled,
    refetchInterval: 5000,
  });

// ============ Main hook ============

export function useConfigData({
  configId,
  studioId,
  bridgeUrl,
  bridgeOnline,
  isActiveConfig,
}: UseConfigDataOptions): UseConfigDataReturn {
  const isOnline = bridgeOnline && isActiveConfig && !!bridgeUrl;
  const mode: ConfigDataMode = isOnline ? "online" : "offline";

  const syncBridgeConnections = useCallback(async () => {
    if (!bridgeUrl) return;
    try {
      await fetch(`${bridgeUrl}/api/sync/pull`, { method: "POST" });
    } catch (error) {
      console.error("Failed to trigger bridge connection pull:", error);
    }
  }, [bridgeUrl]);

  // --- Online hooks (always called, but enabled conditionally) ---
  const bridgeConnections = useBridgeConnections(isOnline ? bridgeUrl : null);
  const studioConnections = useStudioConnections(studioId);
  const createStudioConnection = useCreateStudioConnection();
  const updateStudioConnection = useUpdateStudioConnection();
  const deleteStudioConnection = useDeleteStudioConnection();
  const connectDev = useConnectDevice(isOnline ? bridgeUrl : null);
  const disconnectDev = useDisconnectDevice(isOnline ? bridgeUrl : null);
  const bridgeLayouts = useBridgeLayouts(bridgeUrl, isOnline);
  const bridgeProfiles = useBridgeProfiles(bridgeUrl, isOnline);
  const bridgePhysConns = useBridgePhysicalConnections(
    isOnline ? bridgeUrl : null,
  );
  const createBridgePhysConn = useCreatePhysicalConnection(
    isOnline ? bridgeUrl : null,
  );
  const deleteBridgePhysConn = useDeletePhysicalConnection(
    isOnline ? bridgeUrl : null,
  );

  // --- Online layout/profile mutations ---
  const queryClient = useQueryClient();

  const createBridgeLayout = useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const res = await fetch(`${bridgeUrl}/api/layouts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Failed to create layout: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-layouts", bridgeUrl],
      });
      toast.success("Layout created");
    },
    onError: (err: Error) =>
      toast.error("Failed to create layout: " + err.message),
  });

  const deleteBridgeLayout = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${bridgeUrl}/api/layouts/${id}`, {
        method: "DELETE",
      });
      if (!res.ok && res.status !== 204)
        throw new Error(`Failed to delete layout: ${res.status}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-layouts", bridgeUrl],
      });
      toast.success("Layout deleted");
    },
    onError: (err: Error) =>
      toast.error("Failed to delete layout: " + err.message),
  });

  const createBridgeProfile = useMutation({
    mutationFn: async (data: ProfileInsert) => {
      const res = await fetch(`${bridgeUrl}/api/profiles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Failed to create profile: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-profiles", bridgeUrl],
      });
      toast.success("Profile created");
    },
    onError: (err: Error) =>
      toast.error("Failed to create profile: " + err.message),
  });

  const deleteBridgeProfile = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${bridgeUrl}/api/profiles/${id}`, {
        method: "DELETE",
      });
      if (!res.ok && res.status !== 204)
        throw new Error(`Failed to delete profile: ${res.status}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-profiles", bridgeUrl],
      });
      toast.success("Profile deleted");
    },
    onError: (err: Error) =>
      toast.error("Failed to delete profile: " + err.message),
  });

  // --- Offline hooks ---
  const { data: config, isLoading: configLoading } = useSingleStudioConfig(
    !isOnline ? configId : undefined,
  );
  const updateConfig = useUpdateStudioConfig();

  const [localData, setLocalData] = useState<StudioConfigData | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDataRef = useRef<StudioConfigData | null>(null);

  useEffect(() => {
    if (!isOnline && config?.config_data) {
      setLocalData(config.config_data as unknown as StudioConfigData);
    }
  }, [isOnline, config?.config_data]);

  // Flush any pending debounced persist on unmount (e.g. navigating away)
  useEffect(() => {
    return () => {
      if (debounceRef.current && pendingDataRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        updateConfig.mutate({
          id: configId,
          studio_id: studioId,
          config_data: pendingDataRef.current,
        });
        pendingDataRef.current = null;
      }
    };
  }, [configId, studioId, updateConfig]);

  const persistOffline = useCallback(
    (newData: StudioConfigData) => {
      // Update local state immediately for responsive UI
      setLocalData(newData);
      // Debounce the actual DB write to batch rapid edits into one version
      pendingDataRef.current = newData;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        pendingDataRef.current = null;
        updateConfig.mutate({
          id: configId,
          studio_id: studioId,
          config_data: newData,
        });
      }, 2000);
    },
    [configId, studioId, updateConfig],
  );

  const now = () => new Date().toISOString();
  const connectionStatusMap = new Map<string, ConnectionStatus>(
    (bridgeConnections.data ?? []).map((connection: ConnectionWithStatus) => [
      connection.id,
      connection.status,
    ]),
  );
  const connections = (studioConnections.data ?? []).map((connection) => ({
    ...connection,
    ...(isOnline
      ? {
          status: connectionStatusMap.get(connection.id) ?? "disconnected",
        }
      : {}),
  }));

  // --- Unified interface ---
  if (isOnline) {
    return {
      mode: "online",
      isLoading:
        studioConnections.isLoading ||
        bridgeLayouts.isLoading ||
        bridgeProfiles.isLoading,
      connections,
      addConnection: (data: ConnectionInsert) => {
        void createStudioConnection
          .mutateAsync({ studio_id: studioId, connection: data })
          .then(() => syncBridgeConnections());
      },
      updateConnection: (id: string, data: ConnectionUpdate) => {
        void updateStudioConnection
          .mutateAsync({ id, studio_id: studioId, connection: data })
          .then(() => syncBridgeConnections());
      },
      deleteConnection: (id: string) => {
        void deleteStudioConnection
          .mutateAsync({ id, studio_id: studioId })
          .then(() => syncBridgeConnections());
      },
      isConnectionMutating:
        createStudioConnection.isPending ||
        updateStudioConnection.isPending ||
        deleteStudioConnection.isPending,
      connectDevice: (id: string) => connectDev.mutate(id),
      disconnectDevice: (id: string) => disconnectDev.mutate(id),
      isConnecting: connectDev.isPending,
      isDisconnecting: disconnectDev.isPending,
      layouts: bridgeLayouts.data ?? [],
      addLayout: (data) => createBridgeLayout.mutate(data),
      deleteLayout: (id) => deleteBridgeLayout.mutate(id),
      profiles: bridgeProfiles.data ?? [],
      addProfile: (data) => createBridgeProfile.mutate(data),
      deleteProfile: (id) => deleteBridgeProfile.mutate(id),
      physicalConnections: bridgePhysConns.data ?? [],
      addPhysicalConnection: (data) => createBridgePhysConn.mutate(data),
      deletePhysicalConnection: (id) => deleteBridgePhysConn.mutate(id),
      getDeviceState: () => undefined,
      setDeviceState: () => {},
      updateDeviceStateValue: () => {},
      isMutating:
        createStudioConnection.isPending ||
        updateStudioConnection.isPending ||
        deleteStudioConnection.isPending ||
        createBridgeLayout.isPending ||
        deleteBridgeLayout.isPending ||
        createBridgeProfile.isPending ||
        deleteBridgeProfile.isPending ||
        createBridgePhysConn.isPending ||
        deleteBridgePhysConn.isPending,
    };
  }

  // Offline mode
  const offlineLayouts = localData?.layouts ?? [];
  const offlineProfiles = localData?.profiles ?? [];

  return {
    mode: "offline",
    isLoading: configLoading || studioConnections.isLoading,
    connections,
    addConnection: (data: ConnectionInsert) => {
      void createStudioConnection.mutateAsync({
        studio_id: studioId,
        connection: data,
      });
    },
    updateConnection: (id: string, data: ConnectionUpdate) => {
      void updateStudioConnection.mutateAsync({
        id,
        studio_id: studioId,
        connection: data,
      });
    },
    deleteConnection: (id: string) => {
      void deleteStudioConnection.mutateAsync({ id, studio_id: studioId });
    },
    isConnectionMutating:
      createStudioConnection.isPending ||
      updateStudioConnection.isPending ||
      deleteStudioConnection.isPending,
    layouts: offlineLayouts,
    addLayout: (data: { name: string; description?: string }) => {
      if (!localData) return;
      const newLayout: LayoutWithChildren = {
        id: generateUUID(),
        name: data.name,
        description: data.description,
        art_enabled: false,
        art_placement: "background",
        art_premultiplied_key: false,
        art_clip: 0,
        art_gain: 1000,
        art_invert_key: false,
        border_enabled: false,
        boxes: [],
        presets: [],
        created_at: now(),
        updated_at: now(),
      };
      persistOffline({
        ...localData,
        layouts: [...localData.layouts, newLayout],
      });
    },
    deleteLayout: (id: string) => {
      if (!localData) return;
      persistOffline({
        ...localData,
        layouts: localData.layouts.filter((l) => l.id !== id),
      });
    },
    profiles: offlineProfiles,
    addProfile: (data: ProfileInsert) => {
      if (!localData) return;
      const newProfile: Profile = {
        id: generateUUID(),
        ...data,
        created_at: now(),
        updated_at: now(),
      };
      persistOffline({
        ...localData,
        profiles: [...localData.profiles, newProfile],
      });
    },
    deleteProfile: (id: string) => {
      if (!localData) return;
      persistOffline({
        ...localData,
        profiles: localData.profiles.filter((p) => p.id !== id),
      });
    },
    physicalConnections: localData?.physical_connections ?? [],
    addPhysicalConnection: (data: PhysicalConnectionInsert) => {
      if (!localData) return;
      const newPC: PhysicalConnection = {
        id: generateUUID(),
        ...data,
        created_at: now(),
        updated_at: now(),
      };
      persistOffline({
        ...localData,
        physical_connections: [
          ...(localData.physical_connections ?? []),
          newPC,
        ],
      });
    },
    deletePhysicalConnection: (id: string) => {
      if (!localData) return;
      const remaining = (localData.physical_connections ?? []).filter(
        (pc) => pc.id !== id,
      );
      persistOffline({
        ...localData,
        physical_connections: remaining.length > 0 ? remaining : undefined,
      });
    },
    getDeviceState: (connectionId: string) =>
      localData?.device_states?.[connectionId],
    setDeviceState: (connectionId: string, store: DeviceStateStore) => {
      if (!localData) return;
      persistOffline({
        ...localData,
        device_states: {
          ...(localData.device_states ?? {}),
          [connectionId]: store,
        },
      });
    },
    updateDeviceStateValue: (
      connectionId: string,
      path: string,
      value: string | number | boolean,
    ) => {
      if (!localData) return;
      const existing = localData.device_states?.[connectionId];
      if (!existing) return;
      persistOffline({
        ...localData,
        device_states: {
          ...(localData.device_states ?? {}),
          [connectionId]: {
            values: { ...existing.values, [path]: value },
          },
        },
      });
    },
    isMutating:
      updateConfig.isPending ||
      createStudioConnection.isPending ||
      updateStudioConnection.isPending ||
      deleteStudioConnection.isPending,
  };
}
